import type { ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';

interface AdminShellProps {
  title: string;
  subtitle?: string;
  rightSlot?: ReactNode;
  children: ReactNode;
}

const tabs: { to: string; label: string; end?: boolean; external?: boolean }[] = [
  { to: '/admin', label: 'Stats', end: true },
  { to: '/admin/workspaces', label: 'Workspaces' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/links', label: 'Links' },
  { to: '/admin/emails', label: 'Emails' },
  { to: '/admin/web-watcher', label: 'Web Watcher', external: true },
];

export default function AdminShell({ title, subtitle, rightSlot, children }: AdminShellProps) {
  return (
    <div className="min-h-screen flex flex-col bg-bg font-body">
      <header
        className="sticky top-0 z-20 bg-surface"
        style={{ borderBottom: '1px solid var(--color-hairline)' }}
      >
        <div
          className="flex items-center"
          style={{ gap: 16, padding: '14px 32px', minHeight: 60 }}
        >
          <div className="flex items-center gap-2.5">
            <span
              className="inline-flex items-center justify-center font-display font-bold"
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: 'var(--color-ink)',
                color: '#fff',
                fontSize: 14,
                letterSpacing: '-0.02em',
              }}
            >
              go/
            </span>
            <div className="text-[13px]">
              <div className="font-bold text-ink leading-tight">Tidy Go Links</div>
              <div className="text-ink-muted leading-tight" style={{ fontSize: 11 }}>
                Admin console
              </div>
            </div>
          </div>

          <div className="flex-1" />

          <Link
            to="/dashboard"
            className="text-[13px] font-semibold no-underline transition-colors duration-150"
            style={{ color: 'var(--color-ink-soft)' }}
          >
            ← Back to dashboard
          </Link>
          {rightSlot}
        </div>

        <nav className="flex" style={{ padding: '0 24px', gap: 2 }}>
          {tabs.map(tab =>
            tab.external ? (
              <a
                key={tab.to}
                href={tab.to}
                className="no-underline transition-colors duration-150"
                style={{
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--color-ink-soft)',
                  borderBottom: '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {tab.label}
              </a>
            ) : (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className="no-underline transition-colors duration-150"
                style={({ isActive }) => ({
                  padding: '10px 16px',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--color-ink)' : 'var(--color-ink-soft)',
                  borderBottom: `2px solid ${isActive ? 'var(--accent-solid)' : 'transparent'}`,
                  marginBottom: -1,
                })}
              >
                {tab.label}
              </NavLink>
            )
          )}
        </nav>
      </header>

      <main className="flex-1" style={{ padding: '28px 32px 48px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div className="flex items-end justify-between gap-6 flex-wrap" style={{ marginBottom: 24 }}>
            <div>
              <h1
                className="font-display font-bold text-ink m-0"
                style={{ fontSize: 30, letterSpacing: '-0.02em', lineHeight: 1.1 }}
              >
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-ink-soft mt-1.5 m-0">{subtitle}</p>
              )}
            </div>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
